import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Area, CartesianGrid, ComposedChart, Line, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { colors } from "./colors";
import { formatCompact, formatDay, formatMonth } from "./format";
import { metricOf } from "./metrics";
import { RANGES, buildChartRows, eventImpact, isSameWindow, snapToKey, summarize, weekEndOf } from "./traffic";
import Card, { Title, Subtitle } from "./Card";
import MetricTabs from "./MetricTabs";
import EventMarker from "./EventMarker";
import TrafficTooltip from "./TrafficTooltip";
import EventsRail from "./EventsRail";

const RANGE_LABELS = { "28d": "Last 28 days", "90d": "Last 90 days", "12m": "Last 12 months" };

export default function TrafficChart({ series, current, previous, range, compare, metric, onMetricChange, dropWindowStart, events, changeDays, activeWindow, onWindowSelect }) {
    const [hoveredId, setHoveredId] = useState(null);
    const weekly = range === RANGES[2].value;
    const config = metricOf(metric);
    const compareLabel = compare === "previous" ? "previous period" : "same period last year";
    const rangeStart = current[0]?.date ?? "";

    const rows = useMemo(() => buildChartRows(current, previous, weekly, metric), [current, previous, weekly, metric]);
    const currentSummary = useMemo(() => summarize(current), [current]);
    const previousSummary = useMemo(() => summarize(previous), [previous]);

    const keys = useMemo(() => rows.map((row) => row.date), [rows]);
    const lastKey = keys[keys.length - 1];
    const dropStart = snapToKey(keys, dropWindowStart);

    const visibleUpdates = useMemo(() => events.filter((event) => event.date >= rangeStart), [events, rangeStart]);
    const visibleChangeDays = useMemo(() => changeDays.filter((day) => day.date >= rangeStart), [changeDays, rangeStart]);

    const railItems = useMemo(() => [
        ...visibleUpdates.map((event) => ({ type: "algorithm", id: event.id, date: event.date, event, impact: eventImpact(series, event.date) })),
        ...visibleChangeDays.map((day) => ({ type: "changes", id: `day-${day.date}`, date: day.date, day, impact: eventImpact(series, day.date) })),
    ].sort((a, b) => b.date.localeCompare(a.date)), [visibleUpdates, visibleChangeDays, series]);

    // Page changes collapse into one "N changes" marker per x-axis bucket.
    const changeMarkers = useMemo(() => {
        const byKey = new Map();
        visibleChangeDays.forEach((day) => {
            const key = snapToKey(keys, day.date);
            if (key) byKey.set(key, (byKey.get(key) ?? 0) + day.changeCount);
        });
        return [...byKey.entries()].map(([key, count]) => ({
            id: `chg-${key}`,
            key,
            kind: "changes",
            label: `${formatCompact(count)} changes`,
            ariaLabel: `${count} page changes ${weekly ? `in the week of ${formatDay(key)}` : `on ${formatDay(key)}`}. Filter drops by these changes.`,
            window: { start: key, end: weekly ? weekEndOf(key) : key },
        }));
    }, [visibleChangeDays, keys, weekly]);

    const markers = useMemo(() => {
        const updateMarkers = visibleUpdates
            .map((event) => ({ id: event.id, key: snapToKey(keys, event.date) ?? "", kind: "algorithm", label: event.short, ariaLabel: event.title }))
            .filter((marker) => marker.key);
        return [...updateMarkers, ...changeMarkers].sort((a, b) => a.key.localeCompare(b.key)).map((marker, i) => ({ ...marker, row: i % 2 }));
    }, [visibleUpdates, changeMarkers, keys]);

    const isEmphasized = (marker) => {
        if (!hoveredId) return false;
        if (marker.kind === "algorithm") return hoveredId === marker.id;
        const date = hoveredId.replace("day-", "");
        return !!marker.window && date >= marker.window.start && date <= marker.window.end;
    };

    const handleChartClick = (state) => {
        const key = state?.activeLabel !== undefined ? String(state.activeLabel) : null;
        const marker = changeMarkers.find((candidate) => candidate.key === key);
        if (marker?.window) onWindowSelect(marker.window);
    };

    return (
        <Card aria-labelledby="traffic-heading">
            <Head>
                <div>
                    <Title id="traffic-heading">Organic search traffic</Title>
                    <Subtitle>{RANGE_LABELS[range]} vs. {compareLabel} · site-wide, Google Search</Subtitle>
                </div>
                <Legend aria-label="Chart legend">
                    <li><LegendLine $color={colors.accent} />This period</li>
                    <li><LegendDash />Comparison</li>
                    <li><LegendArea />Drop window</li>
                    <li><LegendBar $color={colors.algorithm} />Google update</li>
                    <li><LegendDots />Page changes (click to filter)</li>
                </Legend>
            </Head>

            <MetricTabs active={metric} current={currentSummary} previous={previousSummary} compareLabel={compareLabel} onChange={onMetricChange} />

            <Body>
                <Plot role="img" aria-label={`${config.label} over time with Google updates and site-wide page changes marked`}>
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={rows} margin={{ top: 48, right: 16, left: 4, bottom: 0 }} onClick={handleChartClick}>
                            <CartesianGrid vertical={false} stroke={colors.line} />
                            <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: colors.chartSubtle }}
                                   tickFormatter={(value) => (weekly ? formatMonth(value) : formatDay(value))} minTickGap={32} dy={6} />
                            <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: colors.chartSubtle }} tickFormatter={config.formatAxis}
                                   width={48} reversed={metric === "position"}
                                   domain={metric === "clicks" || metric === "impressions" ? [0, "auto"] : ["auto", "auto"]} />
                            {dropStart && lastKey && dropStart !== lastKey && (
                                <ReferenceArea x1={dropStart} x2={lastKey} fill={colors.critical} fillOpacity={0.06} strokeOpacity={0} />
                            )}
                            <Tooltip content={<TrafficTooltip metric={metric} weekly={weekly} events={events} changeDays={changeDays} />}
                                     cursor={{ stroke: colors.lineStrong, strokeWidth: 1 }} />
                            <Line type="monotone" dataKey="previous" stroke={colors.chartSubtle} strokeWidth={1.5} strokeDasharray="4 4" dot={false} activeDot={false} isAnimationActive={false} />
                            <Area type="monotone" dataKey="current" stroke={colors.accent} strokeWidth={2} fill={colors.accent} fillOpacity={0.07}
                                  activeDot={{ r: 4, strokeWidth: 2, stroke: "#FFFFFF" }} isAnimationActive={false} />
                            {markers.map((marker) => {
                                const isChange = marker.kind === "changes";
                                const color = isChange ? colors.pageChange : colors.algorithm;
                                const emphasized = isEmphasized(marker);
                                const active = isChange && isSameWindow(marker.window ?? null, activeWindow);
                                return (
                                    <ReferenceLine key={marker.id} x={marker.key} stroke={color} strokeWidth={emphasized || active ? 2 : 1}
                                                   strokeDasharray={isChange ? "2 3" : undefined}
                                                   label={<EventMarker text={marker.label} color={color} row={marker.row} emphasized={emphasized} active={active}
                                                                       ariaLabel={marker.ariaLabel}
                                                                       onSelect={isChange && marker.window ? () => onWindowSelect(marker.window) : undefined} />} />
                                );
                            })}
                        </ComposedChart>
                    </ResponsiveContainer>
                </Plot>
                <EventsRail items={railItems} hoveredId={hoveredId}
                            activeDate={activeWindow && activeWindow.start === activeWindow.end ? activeWindow.start : null}
                            onHover={setHoveredId} onSelectDay={(date) => onWindowSelect({ start: date, end: date })} />
            </Body>
        </Card>
    );
}

const Head = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    padding: 20px 20px 16px;
`;

const Legend = styled.ul`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 16px;
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: 12px;
    color: ${colors.muted};
    li { display: flex; align-items: center; gap: 6px; }
`;

const LegendLine = styled.span`
    width: 16px;
    height: 2px;
    border-radius: 2px;
    background: ${({ $color }) => $color};
`;

const LegendDash = styled.span`
    width: 16px;
    border-top: 1.5px dashed ${colors.subtle};
`;

const LegendArea = styled.span`
    width: 12px;
    height: 12px;
    border-radius: 2px;
    background: ${colors.critical}1a;
    box-shadow: inset 0 0 0 1px ${colors.critical}33;
`;

const LegendBar = styled.span`
    width: 2px;
    height: 12px;
    border-radius: 2px;
    background: ${({ $color }) => $color};
`;

const LegendDots = styled.span`
    height: 12px;
    border-left: 2px dotted ${colors.pageChange};
`;

const Body = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 300px;
    @media (max-width: 1100px) { grid-template-columns: minmax(0, 1fr); }
`;

const Plot = styled.div`
    height: 340px;
    padding: 8px 16px 12px;
    @media (max-width: 600px) { padding: 8px 8px 12px; }
`;
